import React, { useContext, useEffect } from "react";
import { Navigate } from "react-router-dom";
import {
  Button,
  LoadingOverlay,
  Select,
  Space,
  Text,
  Title,
} from "@mantine/core";
import useGetMultiEmployerService from "../../../home/services/api/GetMultiEmployerService";
import Employer from "../../../auth/services/model/Employer";
import { AuthContext } from "../../../../providers/AuthProvider";
import useSwitchDefaultEmployerApiService from "../../api/SwitchDefaultEmployerApiService";
import fetchAuthToken from "../../../../utils/fetchAuthToken";
import { useToasts } from "../../../../providers/notifications/ToastsContext";

interface SelectEmployerInterface {
  value: string;
  label: string;
}

const SwitchEmployerSetting: React.FC = () => {
  const auth = useContext(AuthContext);
  const { showToast } = useToasts();

  const branchesService = useGetMultiEmployerService();

  const [employers, setEmployers] = React.useState<SelectEmployerInterface[]>(
    []
  );
  const [selectedEmployer, setSelectedEmployer] = React.useState<string>("");
  const { isSuccess, isError, isLoading, mutate } =
    useSwitchDefaultEmployerApiService();

  useEffect(() => {
    if (
      branchesService.data &&
      Array.isArray(branchesService.data?.employers)
    ) {
      const employersData: SelectEmployerInterface[] =
        branchesService.data?.employers.map((employer: Employer) => ({
          value: employer.id.toString(),
          label: employer.company_name,
        }));
      setEmployers(employersData);
    }
  }, [branchesService.data]);

  useEffect(() => {
    if (isSuccess) {
      showToast({ strapiId: "employer_switch_success" });
      fetchAuthToken({ expire_current_token: true });
    }

    if (isError) {
      showToast({ strapiId: "employer_switch_failed" });
    }
  }, [isSuccess, isError]);

  useEffect(() => {
    if (auth) {
      setSelectedEmployer(auth.employer.id.toString());
    }
  }, [auth]);

  if (!auth) {
    return <Navigate replace to="/login" />;
  }

  return (
    <>
      <LoadingOverlay visible={isLoading || branchesService.isLoading} />
      <div style={{ maxWidth: "40%" }}>
        <Space h="md" />
        {employers.length > 1 && (
          <>
            <Title order={5}>Accounts</Title>
            <Space h="sm" />
            <Text>Choose an account below that you want to switch to</Text>
            <Select
              required
              disabled={branchesService.isLoading}
              placeholder={
                branchesService.isLoading ? "Loading..." : "Select an account"
              }
              style={{
                marginTop: "8px",
              }}
              data={employers}
              value={selectedEmployer}
              onChange={(value: string) => setSelectedEmployer(value)}
              searchable
              nothingFound="No employers found"
            />
            <Space h="xl" />
            <Button
              name="publishAnnouncementButton"
              fullWidth
              radius="md"
              type="submit"
              loaderPosition="right"
              color="jem-theme"
              loading={branchesService.isLoading || isLoading}
              disabled={
                !selectedEmployer ||
                branchesService.isLoading ||
                parseInt(selectedEmployer, 10) === auth.employer.id ||
                isLoading
              }
              onClick={() =>
                mutate({ employer: parseInt(selectedEmployer, 10) })
              }
            >
              Switch Accounts
            </Button>
          </>
        )}
        {(employers.length < 2 || branchesService.isError) && (
          <Text>You have no accounts to switch to</Text>
        )}
      </div>
    </>
  );
};

export default SwitchEmployerSetting;
